import { Controller } from "@hotwired/stimulus";
import { markSeen, notifyChangelogSeen, type StorageLike } from "./changelog-watermark";

/**
 * Minimal surface of dynamic-table used when scrolling the list names a Current Entry.
 * Avoids a hard import cycle; production table controller implements these members.
 */
interface TableSelectionController {
   readonly selectedValue: string | null;
   selectByValue(value: string | number, behavior?: ScrollBehavior): void;
}

/**
 * Changelog page: mark-seen on connect; table is a jump list; list is one scrollable feed of every
 * Changelog Entry. Choosing a table row jumps the list so that entry's heading sits at the top of
 * the list pane (last entry: as far as scrolling allows). Scrolling the list sets the Current Entry
 * to the entry whose heading is at the top of the list pane (last entry when a scrollable list is
 * pinned at the end) and keeps the table row in step. Does not touch the address bar.
 */
export default class extends Controller {
   static targets = ["entry", "list"];
   static values = {
      app: String,
      newestIdentity: String,
      selectedIdentity: String,
   };

   declare readonly entryTargets: HTMLElement[];
   declare readonly hasListTarget: boolean;
   declare readonly listTarget: HTMLElement;
   declare readonly appValue: string;
   declare readonly newestIdentityValue: string;
   declare readonly hasNewestIdentityValue: boolean;
   declare readonly selectedIdentityValue: string;
   declare readonly hasSelectedIdentityValue: boolean;

   /** While true, list scroll does not push selection back into the table (table-driven jump). */
   private suppressScrollSync = false;

   connect() {
      this.writeMarkSeen();
      // Land the list on the server-named Current Entry (newest, or a known `selected` query).
      const selected = this.hasSelectedIdentityValue ? this.selectedIdentityValue : "";
      if (selected) {
         this.jumpListToIdentity(selected);
      }
   }

   onSelectionChange(event: Event) {
      const detail = (event as CustomEvent<{ value?: string | null; values?: string[] }>).detail;
      const identity = detail?.value ?? detail?.values?.[0];
      // Empty selection: leave every entry on the page (table with require-selection should not clear).
      if (!identity) return;
      this.jumpListToIdentity(identity);
   }

   onListScroll() {
      if (this.suppressScrollSync || !this.hasListTarget) return;

      const identity = this.identityAtListTop();
      if (!identity) return;

      this.syncTableToIdentity(identity);
   }

   private writeMarkSeen() {
      const newest = this.hasNewestIdentityValue ? this.newestIdentityValue : null;
      if (!newest) return;

      const storage = this.resolveStorage();
      if (!storage) return;

      markSeen(storage, this.appValue, newest);
      notifyChangelogSeen(this.appValue);
   }

   /**
    * Jump the list so the named Changelog Entry's heading sits at the top of the list pane.
    * A jump to the last entry scrolls as far as the list allows (short last bodies still land).
    */
   private jumpListToIdentity(identity: string) {
      if (!this.hasListTarget) return;

      const entry = this.entryTargets.find((el) => el.dataset.identity === identity);
      if (!entry) return;

      const list = this.listTarget;
      const last = this.entryTargets[this.entryTargets.length - 1];
      const isLast = last === entry;

      this.suppressScrollSync = true;
      try {
         if (isLast) {
            const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);
            list.scrollTo({ top: maxScroll, behavior: "auto" });
         } else {
            const listRect = list.getBoundingClientRect();
            const entryRect = entry.getBoundingClientRect();
            const nextTop = list.scrollTop + (entryRect.top - listRect.top);
            list.scrollTo({ top: Math.max(0, nextTop), behavior: "auto" });
         }
      } finally {
         // Release after the scroll event from this jump has had a chance to fire.
         requestAnimationFrame(() => {
            this.suppressScrollSync = false;
         });
      }
   }

   /**
    * Current Entry from list position: the entry whose heading sits at the top of the list pane.
    * Only when the list can actually scroll and is pinned at the end is the last entry current.
    * A list that does not overflow keeps the heading at the top (first entry). Measured against
    * the list pane, not the window.
    */
   private identityAtListTop(): string | null {
      if (!this.hasListTarget || this.entryTargets.length === 0) return null;

      const list = this.listTarget;
      const entries = this.entryTargets;
      const maxScroll = list.scrollHeight - list.clientHeight;

      if (maxScroll > 0 && list.scrollTop >= maxScroll - 1) {
         return entries[entries.length - 1]!.dataset.identity ?? null;
      }

      const listTop = list.getBoundingClientRect().top;
      let current = entries[0]!;
      for (const entry of entries) {
         // Still inside a body: next heading has not reached the top yet → keep previous.
         if (entry.getBoundingClientRect().top - listTop <= 1) {
            current = entry;
         } else {
            break;
         }
      }
      return current.dataset.identity ?? null;
   }

   private syncTableToIdentity(identity: string) {
      const table = this.tableController();
      if (!table) return;
      if (table.selectedValue === identity) return;
      // Auto: bring the Current Entry's row into view without fighting list scroll.
      table.selectByValue(identity, "auto");
   }

   private tableController(): TableSelectionController | null {
      const tableEl = this.element.querySelector("[data-controller~='dynamic-table']");
      if (!(tableEl instanceof HTMLElement)) return null;

      const controller = this.application.getControllerForElementAndIdentifier(tableEl, "dynamic-table");
      return controller as unknown as TableSelectionController | null;
   }

   private resolveStorage(): StorageLike | null {
      try {
         return window.localStorage;
      } catch {
         return null;
      }
   }
}
